import React from 'react';
import { Play, Star, Clock, X } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { formatDuration, thumbUrl } from '../lib/api';

const EpisodeDetailPopup = ({ open, onOpenChange, episode, episodeRating, allEpisodes, onRate, onWatch, onSelectEpisode }) => {
  if (!episode) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 bg-[#0F0B1E] border-white/10 max-h-[85vh] flex flex-col" data-testid="episode-detail-popup">
        {/* Header: close button */}
        <button onClick={() => onOpenChange(false)} className="absolute right-3 top-3 z-10 p-1 rounded-full bg-black/50 hover:bg-black/80 transition-colors" data-testid="close-episode-popup">
          <X className="w-4 h-4 text-white" />
        </button>

        {/* Fixed top section: Title + Watch Now */}
        <div className="px-5 pt-5 pb-3 shrink-0">
          <p className="text-xs text-[#00FF9D] font-medium mb-1">S{episode.season_number}E{episode.episode_number}</p>
          <h3 className="text-lg font-bold leading-tight mb-3" data-testid="popup-ep-title">{episode.title}</h3>
          <Button
            onClick={() => { onWatch(episode); onOpenChange(false); }}
            className="w-full h-11 bg-[#00FF9D] hover:bg-[#00FF9D]/90 text-black font-bold text-sm rounded-lg"
            data-testid="popup-watch-now-btn"
          >
            <Play className="w-5 h-5 mr-2 fill-black" /> Watch Now
          </Button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pb-4" style={{ maxHeight: '55vh' }}>
          {/* Thumbnail */}
          {episode.thumbnail && (
            <img src={thumbUrl(episode.thumbnail)} alt={episode.title} className="w-full h-40 object-cover rounded-lg mb-3" />
          )}

          {/* Duration */}
          {episode.duration > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
              <Clock className="w-3.5 h-3.5" />
              <span>{formatDuration(episode.duration)}</span>
            </div>
          )}

          {/* Description */}
          {episode.description && (
            <p className="text-sm text-gray-300 leading-relaxed mb-4" data-testid="popup-ep-desc">{episode.description}</p>
          )}

          {/* Rating */}
          <div className="mb-5">
            <p className="text-xs text-gray-500 mb-1.5">Rate this episode</p>
            <div className="flex items-center gap-3">
              <div className="flex gap-1" data-testid="popup-ep-stars">
                {[1, 2, 3, 4, 5].map(v => (
                  <button key={v} onClick={() => onRate(episode.episode_id, v)} className="p-0.5" data-testid={`popup-star-${v}`}>
                    <Star className={`w-6 h-6 ${v <= (episodeRating?.my_rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600 hover:text-yellow-400'} transition-colors`} />
                  </button>
                ))}
              </div>
              {episodeRating?.avg_rating > 0 && (
                <span className="text-xs text-gray-500">{episodeRating.avg_rating.toFixed(1)} ({episodeRating.rating_count})</span>
              )}
            </div>
          </div>

          {/* Other episodes */}
          {allEpisodes.length > 1 && (
            <div>
              <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wider">Other Episodes</p>
              <div className="space-y-1.5">
                {allEpisodes.filter(ep => ep.episode_id !== episode.episode_id).map(ep => (
                  <button
                    key={ep.episode_id}
                    onClick={() => onSelectEpisode(ep)}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
                    data-testid={`popup-other-ep-${ep.episode_id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-[#8B5CF6]">{ep.episode_number}</span>
                    </div>
                    <span className="text-sm flex-1 truncate">{ep.title}</span>
                    <Play className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EpisodeDetailPopup;
